import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft } from 'lucide-react';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center px-4">
        <div className="noise-texture absolute inset-0 pointer-events-none"></div>
        <div className="relative z-10 bg-white rounded-md p-8 w-full max-w-md card-shadow">
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">📧</div>
            <h1 className="font-bebas text-4xl text-emerald-900 tracking-wider mb-2">
              Check Your Email
            </h1>
            <p className="font-manrope text-gray-600">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-md p-4">
              <p className="font-manrope text-sm text-emerald-900">
                <strong>Check your spam folder</strong> if you don't see the email within a few minutes.
              </p>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-md p-4">
              <p className="font-manrope text-sm text-amber-900">
                The reset link expires in <strong>1 hour</strong>.
              </p>
            </div>

            <Link
              to="/login"
              className="flex items-center justify-center gap-2 w-full btn-secondary py-3 rounded-md"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4">
      <div className="noise-texture absolute inset-0 pointer-events-none"></div>
      <div className="relative z-10 bg-white rounded-md p-8 w-full max-w-md card-shadow">
        <div className="text-center mb-6">
          <div className="text-6xl mb-2">🔑</div>
          <h1 className="font-bebas text-4xl text-emerald-900 tracking-wider">
            Forgot Password
          </h1>
          <p className="font-manrope text-gray-600">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="font-manrope">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your-email@example.com"
              required
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-3 rounded-md"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 mt-6 font-manrope text-gray-600 hover:text-emerald-900"
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>
      </div>
    </div>
  );
}
